import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Sparkles, X, ArrowRight, Check } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { TIER_LABEL, TIER_PRICE_USD } from "@/lib/tier";
import { _registerUpgradeModalListener } from "@/lib/upgradeModal";
import Microsurvey from "@/components/Microsurvey";

// localStorage key tracking upgrade-modal dismissals across the session.
// When count hits 3 we fire the microsurvey instead of just closing — this
// turns silent churn into actionable signal without nagging from request #1.
const DISMISS_KEY = "bl_upgrade_dismiss_count";
const SURVEY_FIRED_KEY = "bl_upgrade_survey_fired";

// Static value-prop bullets per tier so the modal can show "here's what you
// get". Kept lightweight; full feature matrix lives on /pricing.
const TIER_BULLETS = {
    investor_lite: [
        "Full inspection report with prioritised repairs",
        "Repair-cost estimates calibrated to your ZIP",
        "Sharable report link for your agent",
    ],
    investor_pro: [
        "Everything in Lite",
        "Deal comparison across multiple properties",
        "Investor-grade ROI + negotiation summary",
        "PDF export + agent-ready handouts",
    ],
    investor_plus: [
        "Everything in Pro",
        "Unlimited reports & portfolios",
        "Investor notes + contractor estimate routing",
        "Priority email support",
    ],
};

const DEFAULT_REASON = {
    full_report: "Unlock the full report — prioritised repairs, cost estimates and your negotiation summary.",
    pdf_export: "Download a polished, agent-ready PDF of this inspection.",
    share_report: "Share a clean copy of your report with your agent or lender.",
    deal_compare: "Compare multiple properties side-by-side to find the strongest deal.",
    investor_notes: "Capture private notes and follow-ups against each property.",
    contractor_routing: "Route repair items to local contractors for live estimates.",
};

export default function UpgradePromptModal() {
    const { user } = useAuth();
    const [payload, setPayload] = useState(null);
    const [surveyOpen, setSurveyOpen] = useState(false);

    useEffect(() => {
        return _registerUpgradeModalListener((p) => setPayload(p || {}));
    }, []);

    if (!payload && !surveyOpen) return null;

    const close = () => {
        // Track dismissals. After 3 and only once per session, fire the
        // microsurvey to learn WHY they keep dismissing.
        try {
            const n = (parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10) || 0) + 1;
            localStorage.setItem(DISMISS_KEY, String(n));
            const alreadyFired = sessionStorage.getItem(SURVEY_FIRED_KEY) === "1";
            if (n >= 3 && !alreadyFired) {
                sessionStorage.setItem(SURVEY_FIRED_KEY, "1");
                setPayload(null);
                setSurveyOpen(true);
                return;
            }
        } catch {
            /* ignore */
        }
        setPayload(null);
    };
    const close_on_backdrop = (e) => {
        if (e.target === e.currentTarget) close();
    };

    if (!payload && surveyOpen) {
        return (
            <Microsurvey
                trigger="upgrade_modal_dismissed_3x"
                onClose={() => setSurveyOpen(false)}
            />
        );
    }

    const minTier = payload.minTier || "investor_pro";
    const tierName = TIER_LABEL[minTier] || "Investor Pro";
    const price = TIER_PRICE_USD[minTier];
    const bullets = TIER_BULLETS[minTier] || TIER_BULLETS.investor_pro;
    const headline =
        payload.title
        || (user?.tier?.tier && user.tier.tier !== "none"
            ? `Upgrade to ${tierName}`
            : `Unlock with ${tierName}`);
    const reason =
        payload.reason
        || (payload.feature && DEFAULT_REASON[payload.feature])
        || "You've hit a feature reserved for paid plans.";

    return (
        <div
            className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-150"
            onClick={close_on_backdrop}
            data-testid="upgrade-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="upgrade-modal-title"
        >
            <div className="bl-card max-w-md w-full overflow-hidden relative shadow-2xl">
                <button
                    type="button"
                    onClick={close}
                    aria-label="Close"
                    className="absolute top-3 right-3 p-1.5 rounded-full text-bl-muted hover:text-bl hover:bg-bl-bg transition-colors"
                    data-testid="upgrade-modal-close"
                >
                    <X className="w-4 h-4" />
                </button>

                {/* Header — tinted band with the lock icon + tier badge */}
                <div className="bg-bl-teal-tint px-6 pt-7 pb-5">
                    <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/70 text-[10px] tracking-[0.2em] uppercase text-bl-primary font-semibold">
                        <Sparkles className="w-3 h-3" />
                        {tierName}
                    </div>
                    <h2
                        id="upgrade-modal-title"
                        className="mt-3 font-display font-semibold text-2xl text-bl leading-tight"
                    >
                        {headline}
                    </h2>
                    <p className="mt-2 text-sm text-bl-muted leading-relaxed">
                        {reason}
                    </p>
                </div>

                {/* Body — feature bullets + price */}
                <div className="px-6 py-5">
                    <ul className="space-y-2.5" data-testid="upgrade-modal-bullets">
                        {bullets.map((b) => (
                            <li
                                key={b}
                                className="flex items-start gap-2.5 text-sm text-bl leading-snug"
                            >
                                <Check
                                    className="w-4 h-4 mt-0.5 text-bl-primary shrink-0"
                                    aria-hidden
                                />
                                <span>{b}</span>
                            </li>
                        ))}
                    </ul>

                    {price && (
                        <div className="mt-5 flex items-baseline gap-2">
                            <span className="font-display font-semibold text-3xl text-bl tnum">
                                ${price}
                            </span>
                            <span className="text-xs text-bl-muted">/ month</span>
                            <span className="text-[10px] tracking-[0.18em] uppercase text-bl-muted ml-2">
                                cancel anytime
                            </span>
                        </div>
                    )}

                    <div className="mt-5 flex items-center gap-2">
                        <Link
                            to={`/pricing?from=${encodeURIComponent(payload.source || payload.feature || "modal")}&tier=${minTier}`}
                            onClick={() => {
                                try { localStorage.setItem(DISMISS_KEY, "0"); } catch { /* ignore */ }
                                setPayload(null);
                            }}
                            className="bl-btn-primary flex-1 inline-flex items-center justify-center gap-2"
                            data-testid="upgrade-modal-cta"
                        >
                            See plans
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                        <button
                            type="button"
                            onClick={close}
                            className="bl-btn-ghost text-sm px-4"
                            data-testid="upgrade-modal-dismiss"
                        >
                            Not now
                        </button>
                    </div>

                    <div className="mt-3 text-[10px] text-bl-muted text-center flex items-center justify-center gap-1.5">
                        <Lock className="w-3 h-3" aria-hidden />
                        Secure checkout · Stripe · 30-day money back
                    </div>
                </div>
            </div>
        </div>
    );
}
